import type { MutableRefObject } from 'react'
import type { TextChain } from '@/api/types/stowed-element'
import type { ChainTextSelection } from '@/components/app/shapes/shape-text-editor/hooks/use-shape-text-selection'
import type { ShapeTextEditorRef } from '@/components/app/shapes/shape-text-editor'
import { groupHoverStyleForShape } from '@/components/app/editor/selection-chrome/group-hover-fill'
import { computeIsolationOpacityMultiplierForOutsiders } from '@/lib/groups/group-isolation-opacity'
import { isDescendantOfGroup } from '@/lib/groups/shape-group-tree'
import { isGroupShape } from '@/lib/groups/group-kind'
import {
  isShapeFrozenForGeometry,
  isShapeLockedForUserEdit,
} from '@/lib/groups/element-edit-policy'
import type { ShapeBounds } from '@/lib/shapes/types/shape-bounds'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { ShapeTextWrapContext } from '@/lib/shapes/types/shape-text-wrap-context'
import {
  chainContaining,
  hasIncomingLink,
  hasOutgoingLink,
} from '@/lib/text-linking/text-chain-mutator'
import { isShapeSelectable } from './selection-scope'
import type { ShapeLayerPresentation, ShapeLayerText } from '../types/shape-layer-item-props'
import type { ShapeManagerProps } from '../types/shape-manager-props'

/** The `ShapeManager` state one render reads to derive every painted shape's inputs. */
export interface ShapeLayerRenderState {
  shapesById: Map<string, ShapeData>
  selectedIdSet: Set<string>
  selectedShapeIds: string[]
  selectedShape: ShapeData | null
  isSingleSelect: boolean
  isResizeGestureActive: boolean
  altHoveredShapeId: string | null
  frozenOutlinePulse: { shapeId: string; key: number } | null
  activeGroupIsolationId: string | null
  activeIsolationAncestorDepthById: Map<string, number> | null
  /**
   * The edit scope (isolated group) for this render. Taken from the prop, not `editScopeGroupIdRef`:
   * that ref is synced in an effect, so on the render that enters or leaves isolation it still holds
   * the previous scope, and the memoized items would keep that stale selectability until some
   * unrelated change re-rendered them.
   */
  editScopeGroupId: string | null
  elementZIndexMap: ShapeManagerProps['elementZIndexMap']
  isInShapeCreationMode: boolean
  scale: number
  shapeBounds: Record<string, ShapeBounds>
  textOnPathPreview: ShapeManagerProps['textOnPathPreview']
  editingShapeTextId: string | null
  shapeTextEditorRef: MutableRefObject<ShapeTextEditorRef | null>
  shapeTextWrapContext: ShapeTextWrapContext | undefined
  shapeTextCaretClientPoint: { x: number; y: number } | null
  shapeTextCaretOffset: number | null
  suppressShapeTextExitOnBlur: boolean
  liveChains: TextChain[]
  chainFrameIdsByShapeId: Map<string, string[]>
  chainSelectionRangeByShapeId: Map<string, { start: number; end: number }>
  chainTextSelection: ChainTextSelection | null
}

/** How `shape` paints this render: selection, frozen outline, isolation dimming, z-order. */
export function shapeLayerPresentation(
  shape: ShapeData,
  state: ShapeLayerRenderState
): ShapeLayerPresentation {
  const {
    shapesById,
    selectedIdSet,
    selectedShapeIds,
    activeGroupIsolationId,
    elementZIndexMap,
    textOnPathPreview,
  } = state

  const isSelected = selectedIdSet.has(shape.id) && selectedShapeIds.includes(shape.id)
  const isFrozenUnlocked =
    isShapeFrozenForGeometry(shape, shapesById) && !isShapeLockedForUserEdit(shape, shapesById)

  return {
    zIndex:
      elementZIndexMap?.get(shape.id) ??
      (elementZIndexMap != null ? elementZIndexMap.size + 1 : undefined),
    scale: state.scale,
    isSelected,
    selectionHighlightFrozen: isSelected && isFrozenUnlocked,
    showPersistentFrozenOutline: !selectedIdSet.has(shape.id) && isFrozenUnlocked,
    frozenOutlinePulseKey:
      state.frozenOutlinePulse?.shapeId === shape.id ? state.frozenOutlinePulse.key : 0,
    isAltHovered: state.altHoveredShapeId === shape.id,
    isPointerInert:
      state.isInShapeCreationMode || !isShapeSelectable(shape, state.editScopeGroupId),
    opacityMultiplier: computeIsolationOpacityMultiplierForOutsiders({
      activeGroupIsolationId,
      shapesById,
      ownParentGroupId: shape.parentGroupId,
      isInsideActiveIsolationSubtree:
        shape.id === activeGroupIsolationId ||
        (activeGroupIsolationId != null &&
          isDescendantOfGroup(shape, activeGroupIsolationId, shapesById)),
      activeIsolationAncestorDepthById: state.activeIsolationAncestorDepthById ?? undefined,
    }),
    groupHoverFill: isGroupShape(shape)
      ? groupHoverStyleForShape(shape, shapesById).fill
      : undefined,
    isResizingThisShape:
      state.isSingleSelect && state.isResizeGestureActive && state.selectedShape?.id === shape.id,
    measuredBounds: state.shapeBounds[shape.id],
    textOnPathPreview: textOnPathPreview?.shapeId === shape.id ? textOnPathPreview : undefined,
  }
}

/**
 * The text editor inputs for `shape`, or `null` when it shows no text.
 *
 * A photo fill wins on the wire (Content is a `oneof` — see CLAUDE.md C1) but doesn't clear
 * `shape.paragraphs` in the live in-memory shape, only at persistence. Without the photo guard, a
 * shape carrying both rendered its stale text on top of the photo until the next reload silently
 * dropped the paragraphs.
 *
 * Shows on `paragraphs != null` alone (not just editing/has-content), independent of selection:
 * `enterShapeTextEdit` commits an empty `paragraphs` array onto the shape the FIRST time it's ever
 * double-clicked into, and that persists after exiting edit — so "has this shape ever been made a
 * text box" (not "is it selected right now") is what should keep giving an empty box a render path
 * for its "Type here..." placeholder, even idle and deselected. `ShapeTextEditor` itself gates the
 * placeholder to `!isEditing` so it disappears the moment real editing starts.
 */
export function shapeLayerText(
  shape: ShapeData,
  state: ShapeLayerRenderState
): ShapeLayerText | null {
  const isEditing = state.editingShapeTextId === shape.id

  if (shape.hidden || shape.photoFill != null || (!isEditing && shape.paragraphs == null)) {
    return null
  }

  const uid = shape.uid != null ? String(shape.uid) : null

  return {
    isEditing,
    wrap: state.shapeTextWrapContext,
    editorRef: isEditing ? state.shapeTextEditorRef : null,
    caretClientPoint: isEditing ? state.shapeTextCaretClientPoint : null,
    caretOffset: isEditing ? state.shapeTextCaretOffset : null,
    suppressExitOnBlur: isEditing ? state.suppressShapeTextExitOnBlur : false,
    chainFrameIds: state.chainFrameIdsByShapeId.get(shape.id),
    chainSelectionRange: state.chainSelectionRangeByShapeId.get(shape.id) ?? null,
    hasChainSelection: state.chainTextSelection != null,
    hasNextInChain: uid != null && hasOutgoingLink(state.liveChains, uid),
    hasPrevInChain: uid != null && hasIncomingLink(state.liveChains, uid),
    isInChain: uid != null && Boolean(chainContaining(state.liveChains, uid)),
  }
}
